// src/components/PPMViewerPro.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase/firebaseConfig";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import PhotoGallery from "./PhotoGallery";

const PRIORITY_COLORS = {
  Urgent: "bg-red-500",
  High: "bg-amber-500",
  Normal: "bg-sky-500",
  Low: "bg-gray-400",
};
const PRIORITY_PILLS = {
  Urgent: "bg-red-50 text-red-700 border-red-200",
  High: "bg-amber-50 text-amber-700 border-amber-200",
  Normal: "bg-sky-50 text-sky-700 border-sky-200",
  Low: "bg-gray-50 text-gray-700 border-gray-200",
};

export default function PPMViewerPro() {
  const auth = getAuth();
  const me = auth.currentUser;
  const displayName = me?.displayName || me?.email || "Engineer";

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(["due", "inprogress"]);
  const [priorityFilter, setPriorityFilter] = useState([
    "Urgent",
    "High",
    "Normal",
    "Low",
  ]);
  const [assetFilter, setAssetFilter] = useState("ALL");
  const [view, setView] = useState("Machines"); // Machines | Board | Calendar

  // Date window
  const today = atMidnight(new Date());
  const [monthCursor, setMonthCursor] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const range = useMemo(() => monthRange(monthCursor, 45), [monthCursor]);

  // Data
  const [assets, setAssets] = useState([]);
  const [jobs, setJobs] = useState([]); // in-range
  const [overdue, setOverdue] = useState([]); // overdue before window
  const [loading, setLoading] = useState(true);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  // Drawer
  const [open, setOpen] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [activePlan, setActivePlan] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");

  // ─────────────────────────── Assets (once)
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "assets"));
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setAssets(list);
      } catch (e) {
        console.error("Failed to load assets:", e);
      }
    })();
  }, []);

  // ─────────────────────────── Jobs (polling)
  const REFRESH_MS = 30 * 60 * 1000; // 30 min

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const q1 = query(
        collection(db, "ppm_jobs"),
        where("dueDate", ">=", range.start),
        where("dueDate", "<=", range.end),
        orderBy("dueDate", "asc")
      );
      const q2 = query(
        collection(db, "ppm_jobs"),
        where("status", "in", ["due", "inprogress", "missed"]),
        where("dueDate", "<", range.start),
        orderBy("dueDate", "asc")
      );

      const [s1, s2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      setJobs(s1.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
      setOverdue(s2.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
      setLastFetchedAt(new Date());
    } catch (e) {
      console.error("Failed to fetch jobs:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end]);

  useEffect(() => {
    const t = setInterval(fetchJobs, REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end]);

  // ─────────────────────────── Derived
  const all = useMemo(() => {
    const merged = dedupeById([...overdue, ...jobs]);
    return merged.filter((j) => {
      if (assetFilter !== "ALL" && j.assetId !== assetFilter) return false;
      if (!statusFilter.includes(j.status)) return false;
      if (!priorityFilter.includes(j.priority || "Normal")) return false;
      const hay = `${j.assetName || ""} ${
        j.title || ""
      } ${j.notes || ""}`.toLowerCase();
      return hay.includes(search.toLowerCase());
    });
  }, [overdue, jobs, search, assetFilter, statusFilter, priorityFilter]);

  const buckets = useMemo(() => {
    const o = {
      overdue: [],
      due: [],
      inprogress: [],
      complete: [],
      missed: [],
      skipped: [],
    };
    for (const j of all) {
      const d = j.dueDate?.toDate?.() ?? new Date(8640000000000000);
      const isOverdue =
        (j.status === "due" || j.status === "inprogress") && d < today;
      if (isOverdue) o.overdue.push(j);
      else if (o[j.status]) o[j.status].push(j);
      else o.missed.push(j);
    }
    for (const k of Object.keys(o)) {
      o[k].sort(
        (a, b) => (a.dueDate?.toDate?.() ?? 0) - (b.dueDate?.toDate?.() ?? 0)
      );
    }
    return o;
  }, [all, today]);

  // KPIs
  const kpi = useMemo(() => {
    const dueToday = all.filter((j) => sameDay(j.dueDate?.toDate?.(), today))
      .length;
    const overdueCount = all.filter((j) => {
      const d = j.dueDate?.toDate?.();
      return d && d < today && (j.status === "due" || j.status === "inprogress");
    }).length;
    const next7 = all.filter((j) => {
      const d = j.dueDate?.toDate?.();
      return d && d >= today && d <= addDays(today, 7);
    }).length;
    const done7 = all.filter((j) => {
      const done = j.completedAt?.toDate?.();
      return j.status === "complete" && done && done >= addDays(today, -7);
    }).length;
    return { dueToday, overdueCount, next7, done7 };
  }, [all, today]);

  // ─────────────────────────── Actions
  const openJob = async (job) => {
    setActiveJob(job);
    setNotesDraft(job.notes || "");
    setActivePlan(null);
    setOpen(true);
    if (job.planId) {
      try {
        const p = await getDoc(doc(db, "ppm_plans", job.planId));
        if (p.exists()) setActivePlan({ id: p.id, ...(p.data() || {}) });
      } catch (e) {
        console.error("Failed to load plan", e);
      }
    }
  };

  const setInProgress = async (job) => {
    try {
      await updateDoc(doc(db, "ppm_jobs", job.id), {
        status: "inprogress",
        acknowledgedBy: displayName,
        startedAt: serverTimestamp(),
      });
      patchJobLocal(job.id, { status: "inprogress", acknowledgedBy: displayName });
    } catch (e) {
      console.error("Failed to set in progress", e);
    }
  };

  const completeJob = async (job) => {
    try {
      await updateDoc(doc(db, "ppm_jobs", job.id), {
        status: "complete",
        completedAt: serverTimestamp(),
        notes: notesDraft || job.notes || "",
      });
      patchJobLocal(job.id, {
        status: "complete",
        notes: notesDraft || job.notes || "",
      });
      setOpen(false);
    } catch (e) {
      console.error("Failed to complete job", e);
    }
  };

  const skipJob = async (job) => {
    try {
      await updateDoc(doc(db, "ppm_jobs", job.id), {
        status: "skipped",
        completedAt: serverTimestamp(),
        notes: notesDraft || job.notes || "",
      });
      patchJobLocal(job.id, {
        status: "skipped",
        notes: notesDraft || job.notes || "",
      });
      setOpen(false);
    } catch (e) {
      console.error("Failed to skip job", e);
    }
  };

  const toggleChecklist = async (idx) => {
    if (!activeJob || !activePlan?.checklist) return;

    const current = Array.isArray(activeJob.checklistState)
      ? [...activeJob.checklistState]
      : Array(activePlan.checklist.length).fill(false);

    current[idx] = !current[idx];

    setActiveJob((s) => (s ? { ...s, checklistState: current } : s));
    patchJobLocal(activeJob.id, { checklistState: current });

    try {
      await updateDoc(doc(db, "ppm_jobs", activeJob.id), {
        checklistState: current,
      });
    } catch (e) {
      console.error("Failed to update checklist:", e);
    }
  };

  const patchJobLocal = (id, patch) => {
    setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, ...patch } : j)));
    setOverdue((prev) =>
      prev.map((j) => (j.id === id ? { ...j, ...patch } : j))
    );
  };

  // ─────────────────────────── Render
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="px-6 pt-6 max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">PPM Dashboard</h1>
            <p className="text-gray-500 text-sm">
              Designed by Peter Taylor
              {lastFetchedAt ? (
                <span className="ml-2">
                  ·{" "}
                  <span className="italic">
                    Updated {timeAgo(lastFetchedAt)}
                  </span>
                </span>
              ) : null}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchJobs}
              className="text-sm px-3 py-1.5 rounded-md border bg-white hover:bg-gray-50"
            >
              Refresh
            </button>
            <Tab
              isActive={view === "Machines"}
              onClick={() => setView("Machines")}
            >
              Machines
            </Tab>
            <Tab isActive={view === "Board"} onClick={() => setView("Board")}>
              Board
            </Tab>
            <Tab
              isActive={view === "Calendar"}
              onClick={() => setView("Calendar")}
            >
              Calendar
            </Tab>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 pb-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-6 mt-6">
        {/* Sidebar */}
        <aside className="lg:col-span-1">
          <Card>
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-700">Overview</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <KPI label="Due Today" value={kpi.dueToday} tone="indigo" />
              <KPI label="Overdue" value={kpi.overdueCount} tone="red" />
              <KPI label="Next 7d" value={kpi.next7} tone="amber" />
              <KPI label="Done 7d" value={kpi.done7} tone="emerald" />
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <Label>Search</Label>
                <input
                  className="w-full p-2 border rounded-md"
                  placeholder="Asset, task, note…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div>
                <Label>Asset</Label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={assetFilter}
                  onChange={(e) => setAssetFilter(e.target.value)}
                >
                  <option value="ALL">All assets</option>
                  {assets.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} {a.code ? `(${a.code})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Status</Label>
                <div className="flex flex-wrap gap-2">
                  {["due", "inprogress", "complete", "missed", "skipped"].map(
                    (s) => {
                      const on = statusFilter.includes(s);
                      return (
                        <ToggleChip
                          key={s}
                          on={on}
                          onClick={() =>
                            setStatusFilter((cur) =>
                              on ? cur.filter((x) => x !== s) : [...cur, s]
                            )
                          }
                        >
                          {statusLabel(s)}
                        </ToggleChip>
                      );
                    }
                  )}
                </div>
              </div>

              <div>
                <Label>Priority</Label>
                <div className="flex flex-wrap gap-2">
                  {["Urgent", "High", "Normal", "Low"].map((p) => {
                    const on = priorityFilter.includes(p);
                    return (
                      <ToggleChip
                        key={p}
                        on={on}
                        onClick={() =>
                          setPriorityFilter((cur) =>
                            on ? cur.filter((x) => x !== p) : [...cur, p]
                          )
                        }
                      >
                        {p}
                      </ToggleChip>
                    );
                  })}
                </div>
              </div>

              <div className="pt-2 border-t">
                <Label>Window</Label>
                <div className="flex items-center justify-between">
                  <button
                    className="text-sm px-2 py-1 border rounded hover:bg-gray-50"
                    onClick={() => setMonthCursor(addMonths(monthCursor, -1))}
                  >
                    ← Prev
                  </button>
                  <div className="text-sm text-gray-700">
                    {monthCursor.toLocaleString(undefined, {
                      month: "long",
                      year: "numeric",
                    })}
                  </div>
                  <button
                    className="text-sm px-2 py-1 border rounded hover:bg-gray-50"
                    onClick={() => setMonthCursor(addMonths(monthCursor, 1))}
                  >
                    Next →
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Fetching {fmtDate(range.start)} – {fmtDate(range.end)}
                </p>
              </div>
            </div>
          </Card>
        </aside>

        {/* Main */}
        <main className="lg:col-span-3">
          <Card>
            {loading ? (
              <div className="text-sm text-gray-500">Loading…</div>
            ) : view === "Machines" ? (
              <MachineView
                assets={assets}
                items={all}
                today={today}
                onOpen={openJob}
                onStart={setInProgress}
                onComplete={completeJob}
              />
            ) : view === "Board" ? (
              <BoardView
                buckets={buckets}
                onOpen={openJob}
                onStart={setInProgress}
                onComplete={completeJob}
              />
            ) : (
              <CalendarView
                month={monthCursor}
                items={all}
                filters={{
                  asset: assetFilter,
                  status: statusFilter,
                  priority: priorityFilter,
                  search,
                }}
                onOpen={openJob}
              />
            )}
          </Card>
        </main>
      </div>

      {/* Drawer */}
      <SlideOver
        open={open}
        onClose={() => setOpen(false)}
        title={drawerTitle(activeJob)}
      >
        {activeJob ? (
          <div className="space-y-5">
            <Details job={activeJob} />
            {activePlan?.checklist?.length ? (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Checklist</h4>
                <ul className="space-y-1">
                  {activePlan.checklist.map((item, idx) => {
                    const checked = !!activeJob.checklistState?.[idx];
                    return (
                      <li key={idx} className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleChecklist(idx)}
                          className="mt-1"
                        />
                        <span
                          className={`${
                            checked ? "line-through text-gray-400" : ""
                          }`}
                        >
                          {item}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            {activeJob.sharepoint?.folderItemId ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">Photos</h4>
                  {activeJob.sharepoint?.shareUrl && (
                    <a
                      href={activeJob.sharepoint.shareUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm underline text-indigo-700"
                    >
                      Upload photos
                    </a>
                  )}
                </div>
                <PhotoGallery folderItemId={activeJob.sharepoint.folderItemId} />
              </div>
            ) : null}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                rows={4}
                className="w-full p-2 border rounded-md"
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
              />
              <div className="flex items-center gap-2 mt-3">
                {activeJob.status === "due" && (
                  <Button
                    onClick={() => setInProgress(activeJob)}
                    variant="ghost"
                  >
                    Start
                  </Button>
                )}
                {activeJob.status !== "complete" && (
                  <Button
                    onClick={() => completeJob(activeJob)}
                    variant="success"
                  >
                    Complete
                  </Button>
                )}
                {activeJob.status !== "complete" && (
                  <Button onClick={() => skipJob(activeJob)} variant="ghost">
                    Skip
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </SlideOver>
    </div>
  );
}

/* ─────────────────────────── Machines View (group by asset, expandable) ─────────────────────────── */
function MachineView({ assets, items, today, onOpen, onStart, onComplete }) {
  const [expanded, setExpanded] = useState(null);

  const machines = useMemo(() => {
    const map = new Map();

    // Seed with all assets so machines with 0 jobs still show
    for (const a of assets) {
      map.set(a.id, {
        assetId: a.id,
        assetName: a.name || "Untitled asset",
        code: a.code || "",
        location: a.location || "",
        department: a.department || "",
        jobs: [],
      });
    }

    // Attach jobs
    for (const j of items) {
      const id = j.assetId || "UNASSIGNED";
      if (!map.has(id)) {
        map.set(id, {
          assetId: id,
          assetName: j.assetName || id,
          code: "",
          location: "",
          department: "",
          jobs: [],
        });
      }
      map.get(id).jobs.push(j);
    }

    return Array.from(map.values()).sort((a, b) =>
      (a.assetName || "").localeCompare(b.assetName || "")
    );
  }, [assets, items]);

  if (!machines.length) {
    return <Muted>No machines found.</Muted>;
  }

  return (
    <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
      {machines.map((m) => {
        const total = m.jobs.length;
        const todayCount = m.jobs.filter((j) =>
          sameDay(j.dueDate?.toDate?.(), today)
        ).length;
        const overdueCount = m.jobs.filter((j) => {
          const d = j.dueDate?.toDate?.();
          return (
            d &&
            d < today &&
            (j.status === "due" || j.status === "inprogress")
          );
        }).length;

        const isExpanded = expanded === m.assetId;

        // Group jobs by date (within current window + filters).
        const jobsSorted = [...m.jobs].sort(
          (a, b) =>
            (a.dueDate?.toDate?.() ?? 0) - (b.dueDate?.toDate?.() ?? 0)
        );
        const byDay = new Map();
        for (const j of jobsSorted) {
          const d = j.dueDate?.toDate?.();
          const key = d ? d.toDateString() : "No due date";
          if (!byDay.has(key)) {
            byDay.set(key, {
              date: d,
              items: [],
            });
          }
          byDay.get(key).items.push(j);
        }
        const sections = Array.from(byDay.values()).sort(
          (a, b) => (a.date || 0) - (b.date || 0)
        );

        return (
          <div
            key={m.assetId}
            className="border border-gray-200 rounded-lg bg-gray-50"
          >
            <button
              type="button"
              onClick={() =>
                setExpanded((prev) => (prev === m.assetId ? null : m.assetId))
              }
              className="w-full flex items-center justify-between px-4 py-3 text-left"
            >
              <div className="min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {m.assetName}
                </div>
                <div className="text-xs text-gray-500 flex flex-wrap gap-2">
                  {m.code && <span>Tag: {m.code}</span>}
                  {m.location && <span>Location: {m.location}</span>}
                  {m.department && <span>Dept: {m.department}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <MachineCountPill label="Total" value={total} />
                <MachineCountPill label="Today" value={todayCount} tone="indigo" />
                <MachineCountPill
                  label="Overdue"
                  value={overdueCount}
                  tone={overdueCount > 0 ? "red" : "gray"}
                />
                <span className="text-xs text-gray-500">
                  {isExpanded ? "Hide tasks" : "Show tasks"}
                </span>
                <span className="text-lg text-gray-500">
                  {isExpanded ? "▴" : "▾"}
                </span>
              </div>
            </button>

            {isExpanded && (
              <div className="border-t border-gray-200 bg-white px-4 py-3 space-y-3">
                {total === 0 ? (
                  <Muted>No tasks in this window for this machine.</Muted>
                ) : (
                  sections.map((section, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="text-xs font-semibold text-gray-700">
                        {section.date
                          ? section.date.toLocaleDateString()
                          : "No due date"}
                      </div>
                      <div className="space-y-1">
                        {section.items.map((j) => {
                          const due = j.dueDate?.toDate?.();
                          const pri = j.priority || "Normal";
                          const pill = PRIORITY_PILLS[pri] || PRIORITY_PILLS.Normal;
                          return (
                            <div
                              key={j.id}
                              className="flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2 bg-white shadow-sm"
                            >
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {j.title || "Task"}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {statusLabel(j.status)} ·{" "}
                                  {due ? due.toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }) : "No time"}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span
                                  className={`text-[11px] border rounded-full px-2 py-0.5 ${pill}`}
                                >
                                  {pri}
                                </span>
                                {j.status === "due" && (
                                  <Button
                                    variant="ghost"
                                    onClick={() => onStart(j)}
                                  >
                                    Start
                                  </Button>
                                )}
                                {j.status !== "complete" && (
                                  <Button
                                    variant="success"
                                    onClick={() => onComplete(j)}
                                  >
                                    Complete
                                  </Button>
                                )}
                                <Button onClick={() => onOpen(j)}>Details</Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MachineCountPill({ label, value, tone = "gray" }) {
  const cls = {
    gray: "bg-gray-100 text-gray-700",
    indigo: "bg-indigo-50 text-indigo-700",
    red: "bg-red-50 text-red-700",
  }[tone];
  return (
    <div
      className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}
    >
      {label}: {value}
    </div>
  );
}

/* ─────────────────────────── Board View (unchanged) ─────────────────────────── */
function BoardView({ buckets, onOpen, onStart, onComplete }) {
  const columns = [
    { key: "overdue", title: "Overdue", tone: "red" },
    { key: "due", title: "Due", tone: "indigo" },
    { key: "inprogress", title: "In Progress", tone: "amber" },
    { key: "complete", title: "Completed", tone: "emerald" },
  ];

  return (
    <div className="overflow-x-auto">
      <div className="flex gap-4 min-h-[60vh] pb-1">
        {columns.map((col) => {
          const list = buckets[col.key] || [];
          return (
            <Column
              key={col.key}
              title={col.title}
              count={list.length}
              tone={col.tone}
            >
              {list.length ? (
                list.map((j) => (
                  <JobCard
                    key={j.id}
                    job={j}
                    onOpen={() => onOpen(j)}
                    onStart={() => onStart(j)}
                    onComplete={() => onComplete(j)}
                    showStart={col.key !== "inprogress" && col.key !== "complete"}
                    showCompleteMeta={col.key === "complete"}
                    emphasis={col.key === "overdue"}
                  />
                ))
              ) : (
                <Muted>No items.</Muted>
              )}
            </Column>
          );
        })}
      </div>
    </div>
  );
}

function JobCard({
  job,
  onOpen,
  onStart,
  onComplete,
  showStart = true,
  showCompleteMeta = false,
  emphasis = false,
}) {
  const due = job.dueDate?.toDate?.();
  const dueStr = due ? due.toLocaleDateString() : "—";
  const pri = job.priority || "Normal";
  const pill = PRIORITY_PILLS[pri] || PRIORITY_PILLS.Normal;
  const strip = PRIORITY_COLORS[pri] || PRIORITY_COLORS.Normal;

  return (
    <div
      className={`relative bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition ${
        emphasis ? "ring-2 ring-red-200" : ""
      }`}
    >
      {/* priority color strip */}
      <div className={`absolute left-0 top-0 bottom-0 w-1.5 rounded-l ${strip}`} />
      <div className="pl-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-medium text-gray-900 text-[15px] leading-snug">
              {job.title || "Task"}
            </div>
            <div className="text-xs text-gray-500">
              {job.assetName || job.assetId}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">Due: {dueStr}</div>
            {showCompleteMeta && job.completedAt?.toDate && (
              <div className="text-xs text-emerald-700 mt-0.5">
                Completed: {job.completedAt.toDate().toLocaleString()}
              </div>
            )}
          </div>
          <span
            className={`text-xs border rounded-full px-2 py-0.5 ${pill}`}
          >
            {pri}
          </span>
        </div>

        <div className="flex items-center justify-end gap-2 mt-3">
          {showStart && job.status === "due" && (
            <Button variant="ghost" onClick={onStart}>
              Start
            </Button>
          )}
          {job.status !== "complete" && (
            <Button variant="success" onClick={onComplete}>
              Complete
            </Button>
          )}
          <Button onClick={onOpen}>Details</Button>
        </div>
      </div>
    </div>
  );
}

function Column({ title, count = 0, tone, children }) {
  const border = {
    red: "border-red-200",
    indigo: "border-indigo-200",
    amber: "border-amber-200",
    emerald: "border-emerald-200",
  }[tone] || "border-gray-200";

  return (
    <div
      className={`rounded-xl border ${border} bg-gray-50 flex-shrink-0`}
      style={{ minWidth: "420px", maxWidth: "520px" }}
    >
      <div className="sticky top-0 z-10 bg-gray-50/90 backdrop-blur px-4 py-3 border-b border-gray-200 rounded-t-xl flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-700">
          {title}
        </div>
        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-900 text-white">
          {count}
        </span>
      </div>
      <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">{children}</div>
    </div>
  );
}

/* ─────────────────────────── Calendar View ─────────────────────────── */
function CalendarView({ month, items, filters, onOpen }) {
  const grid = buildMonthGrid(month);
  const filtered = items.filter((j) => {
    if (filters.asset !== "ALL" && j.assetId !== filters.asset) return false;
    if (!filters.status.includes(j.status)) return false;
    if (!filters.priority.includes(j.priority || "Normal")) return false;
    const hay = `${j.assetName || ""} ${
      j.title || ""
    } ${j.notes || ""}`.toLowerCase();
    return hay.includes((filters.search || "").toLowerCase());
  });

  const byDay = new Map();
  for (const j of filtered) {
    const d = j.dueDate?.toDate?.();
    if (!d) continue;
    const key = d.toDateString();
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(j);
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200">
      <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="px-3 py-2">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {grid.map((cell, idx) => {
          const key = cell.date.toDateString();
          const dayJobs = byDay.get(key) || [];
          const isOtherMonth = cell.other;
          return (
            <div
              key={idx}
              className={`h-40 border-b border-r border-gray-200 p-2 ${
                isOtherMonth ? "bg-gray-50" : "bg-white"
              }`}
            >
              <div className="text-xs text-gray-500">
                {cell.date.getDate()}
              </div>
              <div className="mt-1 space-y-1 max-h-32 overflow-y-auto pr-1">
                {dayJobs.length
                  ? dayJobs.slice(0, 5).map((j) => (
                      <button
                        key={j.id}
                        onClick={() => onOpen(j)}
                        className="w-full text-left text-xs px-2 py-1 rounded border hover:bg-gray-50"
                        title={`${j.title} — ${j.assetName || ""}`}
                      >
                        <span className="font-medium">
                          {trim(j.title, 18)}
                        </span>{" "}
                        <span className="text-gray-500">
                          {trim(j.assetName || "", 16)}
                        </span>
                      </button>
                    ))
                  : null}
                {dayJobs.length > 5 && (
                  <div className="text-[11px] text-gray-500">
                    +{dayJobs.length - 5} more…
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────── Drawer bits ─────────────────────────── */
function Details({ job }) {
  const due = job.dueDate?.toDate?.();
  const rows = [
    ["Asset", job.assetName || job.assetId],
    ["Task", job.title || "—"],
    ["Priority", job.priority || "Normal"],
    ["Status", statusLabel(job.status)],
    ["Due", due ? due.toLocaleString() : "—"],
    ["Acknowledged By", job.acknowledgedBy || "—"],
  ];
  return (
    <div className="space-y-2 text-sm">
      {rows.map(([k, v]) => (
        <Row key={k} label={k} value={v} />
      ))}
      {job.notes ? <Block label="Existing Notes" value={job.notes} /> : null}
    </div>
  );
}

function drawerTitle(job) {
  if (!job) return "";
  const due = job.dueDate?.toDate?.();
  const d = due ? due.toLocaleDateString() : "—";
  return `${job.title || "Task"} — ${job.assetName || ""} · Due ${d}`;
}

/* ─────────────────────────── UI atoms ─────────────────────────── */
function Card({ children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
      {children}
    </div>
  );
}
function KPI({ label, value, tone }) {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-700",
    red: "bg-red-50 text-red-700",
    amber: "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-700",
  };
  return (
    <div
      className={`rounded-lg p-4 text-center ${
        tones[tone] || "bg-gray-50 text-gray-700"
      }`}
    >
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs uppercase tracking-wide">{label}</div>
    </div>
  );
}
function Label({ children }) {
  return (
    <div className="text-xs font-semibold text-gray-600 mb-1">{children}</div>
  );
}
function ToggleChip({ on, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-2 py-1 rounded-full border transition ${
        on ? "bg-gray-900 text-white border-gray-900" : "bg-white hover:bg-gray-50"
      }`}
    >
      {children}
    </button>
  );
}
function Tab({ isActive, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`text-sm px-3 py-1.5 rounded-md border ${
        isActive
          ? "bg-gray-900 text-white border-gray-900"
          : "bg-white hover:bg-gray-50"
      }`}
    >
      {children}
    </button>
  );
}
function Button({ children, onClick, variant = "primary" }) {
  const cls = {
    primary: "bg-indigo-600 hover:bg-indigo-700 text-white",
    success: "bg-emerald-600 hover:bg-emerald-700 text-white",
    ghost: "bg-white border border-gray-300 hover:bg-gray-50 text-gray-800",
  }[variant];
  return (
    <button onClick={onClick} className={`text-sm px-3 py-1.5 rounded-md ${cls}`}>
      {children}
    </button>
  );
}
function SlideOver({ open, onClose, title, children }) {
  return (
    <div className={`${open ? "fixed" : "hidden"} inset-0 z-50`}>
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute top-0 right-0 h-full w-full max-w-xl bg-white shadow-xl border-l border-gray-200 flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h4 className="text-base font-semibold text-gray-900">{title}</h4>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            ✕
          </button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
function Muted({ children, className = "" }) {
  return <p className={`text-sm text-gray-500 ${className}`}>{children}</p>;
}
function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <div className="text-gray-500 w-40">{label}</div>
      <div className="text-gray-900 flex-1">{value || "—"}</div>
    </div>
  );
}
function Block({ label, value }) {
  return (
    <div>
      <div className="text-gray-500 mb-1">{label}</div>
      <div className="text-gray-900 whitespace-pre-wrap">{value}</div>
    </div>
  );
}

/* ─────────────────────────── utils ─────────────────────────── */
function atMidnight(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function addMonths(d, n) {
  const x = new Date(d);
  x.setMonth(x.getMonth() + n);
  return x;
}
function sameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function fmtDate(ts) {
  return ts.toDate().toLocaleDateString();
}
function monthRange(anchorMonthDate, padDays) {
  const startMonth = new Date(
    anchorMonthDate.getFullYear(),
    anchorMonthDate.getMonth(),
    1
  );
  const endMonth = new Date(
    anchorMonthDate.getFullYear(),
    anchorMonthDate.getMonth() + 1,
    0
  );
  return {
    start: Timestamp.fromDate(addDays(startMonth, -padDays)),
    end: Timestamp.fromDate(addDays(endMonth, padDays)),
  };
}
function buildMonthGrid(monthDate) {
  const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const end = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
  const startIdx = (start.getDay() + 6) % 7; // Mon=0 … Sun=6
  const cells = [];
  for (let i = 0; i < startIdx; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() - (startIdx - i));
    cells.push({ date: d, other: true });
  }
  for (let d = 1; d <= end.getDate(); d++) {
    cells.push({
      date: new Date(monthDate.getFullYear(), monthDate.getMonth(), d),
      other: false,
    });
  }
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1].date;
    const d = new Date(last);
    d.setDate(d.getDate() + 1);
    cells.push({ date: d, other: true });
  }
  while (cells.length < 42) {
    const last = cells[cells.length - 1].date;
    const d = new Date(last);
    d.setDate(d.getDate() + 1);
    cells.push({ date: d, other: true });
  }
  return cells;
}
function statusLabel(s) {
  return (
    {
      due: "Due",
      inprogress: "In Progress",
      complete: "Completed",
      missed: "Missed",
      skipped: "Skipped",
    }[s] || s
  );
}
function trim(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}
function dedupeById(arr) {
  const seen = new Set();
  const out = [];
  for (const j of arr) {
    if (j && j.id && !seen.has(j.id)) {
      seen.add(j.id);
      out.push(j);
    }
  }
  return out;
}
function timeAgo(d) {
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}
